import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PDFViewer } from "@core/pdf-viewer";
import Modal from "@core/modal";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  Settings,
  CheckCircle2,
  CheckCircle,
  Circle,
  Download,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { updatePdfOptions } from "@packages/store/userSlice";
import { useToast } from "@packages/contexts/ToastContext";

export interface PdfCustomizationSettings {
  showSchoolLogo: boolean;
  schoolLogoPosition: "topLeft" | "topRight";
  madlenLogoPosition: "topLeft" | "center" | "topRight";
  showDate: boolean;
  showTeacherName: boolean;
  showGeneratedImage: boolean;
  fontSize: "small" | "medium" | "large";
}

interface PdfCustomizationModalProps {
  show: boolean;
  onClose: () => void;
  documentComponent: React.ReactElement;
  initialSettings?: Partial<PdfCustomizationSettings>;
  hideSchoolLogoOptions?: boolean;
  hideMadlenLogoOptions?: boolean;
  hideFontSizeOptions?: boolean;
  hideGeneratedImageOptions?: boolean;
}

const defaultSettings: PdfCustomizationSettings = {
  showSchoolLogo: true,
  schoolLogoPosition: "topRight",
  madlenLogoPosition: "center",
  showDate: false,
  showTeacherName: false,
  fontSize: "medium",
  showGeneratedImage: true,
};

const PdfCustomizationModal: React.FC<PdfCustomizationModalProps> = ({
  show,
  onClose,
  hideGeneratedImageOptions = false,
  documentComponent,
  initialSettings = {},
  hideSchoolLogoOptions = false,
  hideMadlenLogoOptions = false,
  hideFontSizeOptions = false,
}) => {
  const { t } = useTranslation();
  const teacherSettings = useAppSelector(
    (state) =>
      state.user.teacher?.preferences?.pdfOptions as PdfCustomizationSettings,
  );
  const [settings, setSettings] = useState<PdfCustomizationSettings>({
    ...defaultSettings,
    ...initialSettings,
    ...teacherSettings,
  });

  const [pdfBlob, setPdfBlob] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const dispatch = useAppDispatch();
  const { addToast } = useToast();

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!show || !documentComponent) return;

    // Skip generating if settings haven't changed — but NOT on first render
    if (
      !isFirstRender.current &&
      JSON.stringify(settings) === JSON.stringify(teacherSettings)
    )
      return;

    const generatePreview = async () => {
      try {
        setIsGenerating(true);
        const documentWithSettings = React.cloneElement(documentComponent, {
          customizationSettings: settings,
        });
        dispatch(updatePdfOptions(settings));
        const blob = await pdf(documentWithSettings).toBlob();
        const url = URL.createObjectURL(blob);
        setPdfBlob(url);
      } catch (error) {
        console.error("PDF preview generation error:", error);
      } finally {
        setIsGenerating(false);
      }
    };

    const timer = setTimeout(() => {
      generatePreview();
    }, 300);

    // Set first render to false after the effect runs
    isFirstRender.current = false;

    return () => {
      clearTimeout(timer);
      if (pdfBlob) {
        URL.revokeObjectURL(pdfBlob);
      }
    };
  }, [show, documentComponent, settings]);

  const handleCheckboxChange = (field: keyof PdfCustomizationSettings) => {
    setSettings((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleOptionChange = (
    field: keyof PdfCustomizationSettings,
    value: string,
  ) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGenerate = async () => {
    setIsDownloading(true);
    try {
      const documentWithSettings = React.cloneElement(documentComponent, {
        customizationSettings: settings,
      });
      const blob = await pdf(documentWithSettings).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      onClose();
    } catch (error) {
      console.error("PDF generation error:", error);
      addToast({
        title: t("common.error"),
        description: t("common.errorDescription"),
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
    // onGenerate(settings);
    // onClose();
  };

  const OptionRadioButton = ({
    id,
    name,
    value,
    checked,
    onChange,
    label,
  }: {
    id: string;
    name: string;
    value: string;
    checked: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    label: string;
  }) => (
    <div className="flex items-center">
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="hidden"
      />
      <label
        htmlFor={id}
        className="flex items-center cursor-pointer gap-2 text-sm transition-colors"
      >
        {checked ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 text-gray-300 dark:text-gray-600" />
        )}
        <span>{label}</span>
      </label>
    </div>
  );

  const OptionCheckbox = ({
    id,
    checked,
    onChange,
    label,
  }: {
    id: string;
    checked: boolean;
    onChange: () => void;
    label: string;
  }) => (
    <div className="flex items-center">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="hidden"
      />
      <label
        htmlFor={id}
        className="flex items-center cursor-pointer gap-2 text-sm transition-colors"
      >
        {checked ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 text-gray-300 dark:text-gray-600" />
        )}
        <span>{label}</span>
      </label>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={t("pdfCustomization.customizePdf")}
      outerClassName="max-w-7xl w-full h-[85vh] overflow-y-auto scroll-smooth"
    >
      <div className="flex flex-col md:flex-row h-full gap-6">
        {/* Left Side: PDF Preview */}
        <div className="flex-1 h-[70vh] border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden flex flex-col">
          {isGenerating || isFirstRender.current ? (
            <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-800 relative overflow-hidden">
              <div className="flex flex-col items-center gap-4">
                {/* Glowing AI-style spinner */}
                <div className="relative h-12 w-12">
                  <div className="absolute inset-0 rounded-full border-2 border-green-500 opacity-30 animate-ping" />
                  <div className="absolute inset-0 rounded-full border-2 border-green-500 animate-spin" />
                </div>

                {/* Animated dots text */}
                <p className="text-gray-500 dark:text-gray-300 text-sm font-medium animate-pulse">
                  {t("pdfCustomization.generatingPreview")}
                  <span className="inline-block animate-bounce">.</span>
                  <span className="inline-block animate-bounce delay-150">
                    .
                  </span>
                  <span className="inline-block animate-bounce delay-300">
                    .
                  </span>
                </p>
              </div>
            </div>
          ) : pdfBlob ? (
            <PDFViewer url={pdfBlob} />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-800">
              <Text variant="p" status="subtle">
                {t("common.errorDescription")}
              </Text>
            </div>
          )}
        </div>

        {/* Right Side: Customization Options */}
        <div className="w-full md:w-64 lg:w-80 min-w-fit flex flex-col h-full">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700 overflow-y-auto flex-1">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <Text variant="h6">
                {t("pdfCustomization.customizationOptions")}
              </Text>
            </div>

            <div className="space-y-6">
              {/* School Logo Settings */}
              {!hideSchoolLogoOptions && (
                <div className="space-y-3">
                  <Text variant="p" weight="medium" className="text-sm">
                    {t("pdfCustomization.schoolLogoSettings")}
                  </Text>
                  <OptionCheckbox
                    id="school-logo-toggle"
                    checked={settings.showSchoolLogo}
                    onChange={() => handleCheckboxChange("showSchoolLogo")}
                    label={t("pdfCustomization.showSchoolLogo")}
                  />

                  {settings.showSchoolLogo && (
                    <div className="ml-6 mt-2 space-y-2">
                      <Text
                        variant="p"
                        className="text-xs text-gray-500 dark:text-gray-400"
                      >
                        {t("pdfCustomization.logoPosition")}
                      </Text>
                      <div className="space-y-2">
                        <OptionRadioButton
                          id="school-logo-top-left"
                          name="schoolLogoPosition"
                          value="topLeft"
                          checked={settings.schoolLogoPosition === "topLeft"}
                          onChange={(e) =>
                            handleOptionChange(
                              "schoolLogoPosition",
                              e.target.value,
                            )
                          }
                          label={t("pdfCustomization.topLeft")}
                        />
                        <OptionRadioButton
                          id="school-logo-top-right"
                          name="schoolLogoPosition"
                          value="topRight"
                          checked={settings.schoolLogoPosition === "topRight"}
                          onChange={(e) =>
                            handleOptionChange(
                              "schoolLogoPosition",
                              e.target.value,
                            )
                          }
                          label={t("pdfCustomization.topRight")}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Material Logo Settings */}
              {!hideMadlenLogoOptions && (
                <div className="space-y-3">
                  <Text variant="p" weight="medium" className="text-sm">
                    {t("pdfCustomization.madlenLogoSettings")}
                  </Text>
                  <div className="ml-0 space-y-2">
                    <Text
                      variant="p"
                      className="text-xs text-gray-500 dark:text-gray-400"
                    >
                      {t("pdfCustomization.logoPosition")}
                    </Text>
                    <div className="space-y-2">
                      <OptionRadioButton
                        id="material-logo-top-left"
                        name="materialLogoPosition"
                        value="topLeft"
                        checked={settings.madlenLogoPosition === "topLeft"}
                        onChange={(e) =>
                          handleOptionChange(
                            "madlenLogoPosition",
                            e.target.value,
                          )
                        }
                        label={t("pdfCustomization.topLeft")}
                      />
                      <OptionRadioButton
                        id="material-logo-center"
                        name="materialLogoPosition"
                        value="center"
                        checked={settings.madlenLogoPosition === "center"}
                        onChange={(e) =>
                          handleOptionChange(
                            "madlenLogoPosition",
                            e.target.value,
                          )
                        }
                        label={t("pdfCustomization.center")}
                      />
                      <OptionRadioButton
                        id="material-logo-top-right"
                        name="materialLogoPosition"
                        value="topRight"
                        checked={settings.madlenLogoPosition === "topRight"}
                        onChange={(e) =>
                          handleOptionChange(
                            "madlenLogoPosition",
                            e.target.value,
                          )
                        }
                        label={t("pdfCustomization.topRight")}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* General Settings */}
              <div className="space-y-3">
                <Text variant="p" weight="medium" className="text-sm">
                  {t("pdfCustomization.generalSettings")}
                </Text>
                <div className="space-y-2">
                  {!hideGeneratedImageOptions && (
                    <OptionCheckbox
                      id="show-generated-image"
                      checked={settings.showGeneratedImage}
                      onChange={() =>
                        handleCheckboxChange("showGeneratedImage")
                      }
                      label={t("pdfCustomization.showGeneratedImage")}
                    />
                  )}
                  <OptionCheckbox
                    id="show-date"
                    checked={settings.showDate}
                    onChange={() => handleCheckboxChange("showDate")}
                    label={t("pdfCustomization.showCreatedDate")}
                  />
                  <OptionCheckbox
                    id="show-teacher-name"
                    checked={settings.showTeacherName}
                    onChange={() => handleCheckboxChange("showTeacherName")}
                    label={t("pdfCustomization.showTeacherName")}
                  />
                </div>
              </div>

              {/* Font Size Settings */}
              {!hideFontSizeOptions && (
                <div className="space-y-3">
                  <Text variant="p" weight="medium" className="text-sm">
                    {t("pdfCustomization.fontSizeSettings")}
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    {["small", "medium", "large"].map((size) => (
                      <Button
                        key={size}
                        size="sm"
                        variant={
                          settings.fontSize === size ? "solid" : "outline"
                        }
                        bgColor={
                          settings.fontSize === size
                            ? "bg-green-100 dark:bg-green-900"
                            : ""
                        }
                        textColor={
                          settings.fontSize === size
                            ? "text-green-700 dark:text-green-300"
                            : ""
                        }
                        borderColor={
                          settings.fontSize === size
                            ? "border-green-300 dark:border-green-700"
                            : ""
                        }
                        rounded="md"
                        onClick={() => handleOptionChange("fontSize", size)}
                      >
                        {t(`pdfCustomization.${size}`)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            size="lg"
            loading={isDownloading}
            disabled={isDownloading}
            variant="solid"
            rounded="lg"
            textColor="text-white"
            bgColor="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
            className="mt-3 sticky bottom-0"
            leftIcon={<Download className="w-5 h-5" />}
          >
            {t("common.generate")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PdfCustomizationModal;
